import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Container,
  Card,
  Table,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import moment from "moment";
import { Chip, Typography } from "@material-ui/core";
import { useHistory } from "react-router";

import {
  getMobileOrders,
  getOrdersNoLoader,
} from "../../actions/mobileOrdersAction";
import useStyles from "../Forms/Listings/Listing/orderDetailsStyles";
import { getOrderDetailsToProcess } from "../../actions/webOrderActions";
import { getUserFromLocalStorage } from "utilities/user";

const MobileWebOrderTable = () => {
  const user = getUserFromLocalStorage();
  const { mobileOrders, isLoading } = useSelector(
    (state) => state.mobileOrders
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const [search, setSearch] = useState("");

  const filteredOrders = (mobileOrders || []).filter((order) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (order?.order_number || order?._id?.slice(-8) || "").toLowerCase().includes(q) ||
      (order?.client_id?.name || "").toLowerCase().includes(q) ||
      (order?.shippingAddress?.customerNumber || "").toLowerCase().includes(q) ||
      (order?.paymentMethod || "").toLowerCase().includes(q) ||
      (order?.orderSrc || "").toLowerCase().includes(q)
    );
  });

  // Handle order navigation based on order type
  const handleOrderManage = async (order) => {
    try {
      if (order.isPackageDelivery) {
        history.push(`/admin/package-delivery/${order._id}`);
        return;
      }
      for (const item of order.orderItems || []) {
        if (
          // Any Lusuku STAFF member on this page (orders:read) routes retailer
          // orders to the staff management view, supervisors included, not
          // only the super user (they used to fall through to nowhere useful).
          (user?.result?.userType === "LUSUKU STAFF" &&
            item.vendorId &&
            item.vendorId.operationMode === "RETAILER") ||
          mobileOrders[0]?.isMultiRetailer
        ) {
          await history.push(`/admin/vendorOrders-mng/${order._id}`);
          return;
        }
        if (item.vendorId && item.vendorId.operationMode === "RESTAURANT") {
          await history.push(`/admin/restaurantOrders-mng/${order._id}`);
          return;
        }
      }
      await history.push(`/admin/orderDetails/${order._id}`);
    } catch (error) {
      console.error("Error navigating:", error);
    }
  };

  const tableStyles = {
    pageContainer: {
      padding: "30px",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
    },
    card: {
      borderRadius: "12px",
      border: "1px solid #e8eaed",
      backgroundColor: "white",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      overflow: "hidden",
    },
    cardHeader: {
      background: "#ffffff",
      borderBottom: "2px solid #f0f2f5",
      padding: "28px 32px",
    },
    cardTitle: {
      color: "#1a202c",
      fontSize: "26px",
      fontWeight: "700",
      marginBottom: "8px",
      letterSpacing: "-0.5px",
    },
    cardSubtitle: {
      color: "#718096",
      fontSize: "15px",
      margin: 0,
    },
    tableContainer: {
      padding: "0",
    },
    table: {
      marginBottom: 0,
      width: "100%",
      tableLayout: "auto",
    },
    tableHeader: {
      backgroundColor: "#2d3748",
      color: "#ffffff",
      fontSize: "10px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      padding: "12px 6px",
      borderBottom: "3px solid #1a202c",
      borderRight: "1px solid #4a5568",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      whiteSpace: "nowrap",
    },
    tableCell: {
      padding: "12px 6px",
      fontSize: "12px",
      color: "#2d3748",
      borderBottom: "1px solid #e8eaed",
      verticalAlign: "middle",
      maxWidth: "150px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    actionButton: {
      padding: "6px",
      borderRadius: "5px",
      border: "2px solid #e2e8f0",
      backgroundColor: "#ffffff",
      color: "#4a5568",
      transition: "all 0.25s ease",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: "13px",
      minWidth: "auto",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  // Fetching the mobile orders with redux & Polling // Deploy useInterval here
  useEffect(() => {
    dispatch(getMobileOrders());
    // Fetching the mobile orders after 1 Minute
    const interval = setInterval(() => {
      // dispatch(getCashOnDeliveryOrders());
      dispatch(getOrdersNoLoader());
    }, process.env.REACT_APP_SET_INTERVAL_POLLING);

    return () => clearInterval(interval);
  }, []);

  //RESTRICTED VIEW FOR ADMINS ONLY
  if (
    user?.result?.user_role === "CLIENT" ||
    user?.result?.user_role === "CREATOR"
  ) {
    return (
      <>
        <Container fluid>
          <Row>
            <Col md={6}>
              <Typography elevation={6} variant="h5" color="error">
                <i className="fas fa-users"></i>&nbsp;&nbsp; Restricted View For
                Admins Only!
              </Typography>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <Spinner
          animation="grow"
          variant="danger"
          style={{
            marginTop: "70px",
            top: "50%",
            left: "50%",
            position: "fixed",
          }}
        />
      ) : (
        <div style={tableStyles.pageContainer}>
          <Container fluid>
            <Row>
              <Col md="12">
                <Card style={tableStyles.card}>
                  <Card.Header style={tableStyles.cardHeader}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                      <div>
                        <h4 style={tableStyles.cardTitle}>Orders History</h4>
                        <p style={tableStyles.cardSubtitle}>Complete order history from mobile app and web</p>
                      </div>
                      <div style={{ position: "relative", minWidth: "260px" }}>
                        <i
                          className="fas fa-search"
                          style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#a0aec0", fontSize: "13px", pointerEvents: "none" }}
                        />
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search order, customer, phone..."
                          style={{
                            paddingLeft: "32px",
                            paddingRight: "10px",
                            paddingTop: "7px",
                            paddingBottom: "7px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            fontSize: "13px",
                            width: "100%",
                            outline: "none",
                            color: "#2d3748",
                            backgroundColor: "#f8f9fa",
                          }}
                        />
                        {search && (
                          <span
                            onClick={() => setSearch("")}
                            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#a0aec0", fontSize: "14px" }}
                          >
                            ✕
                          </span>
                        )}
                      </div>
                    </div>
                    {search && (
                      <p style={{ fontSize: "12px", color: "#718096", marginTop: "8px", marginBottom: 0 }}>
                        Showing {filteredOrders.length} of {mobileOrders?.length || 0} orders
                      </p>
                    )}
                  </Card.Header>
                  <div style={tableStyles.tableContainer}>
                    <Table style={tableStyles.table} responsive striped hover>
                      <thead>
                        <tr>
                          <th style={tableStyles.tableHeader}>#</th>
                          <th style={tableStyles.tableHeader}>Order No.</th>
                          <th style={tableStyles.tableHeader}>Order Src</th>
                          <th style={tableStyles.tableHeader}>Cart Price</th>
                          <th style={tableStyles.tableHeader}>Shipping Zone</th>
                          <th style={tableStyles.tableHeader}>Payment Mode</th>
                          <th style={tableStyles.tableHeader}>Customer No.</th>
                          <th style={tableStyles.tableHeader}>Customer Name</th>
                          <th style={tableStyles.tableHeader}>Order Date</th>
                          <th style={tableStyles.tableHeader}>Payament Status</th>
                          <th style={tableStyles.tableHeader}>Delivery Status</th>
                          <th style={{...tableStyles.tableHeader, borderRight: "none"}}>View</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredOrders.length === 0 ? (
                          <tr>
                            <td colSpan={12} style={{ textAlign: "center", padding: "32px", color: "#718096", fontSize: "14px" }}>
                              No orders match &ldquo;{search}&rdquo;
                            </td>
                          </tr>
                        ) : filteredOrders.map((order, index) => (
                          <tr 
                            key={order?._id}
                            style={{
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#e8eaed";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                            }}
                          >
                            <td style={{...tableStyles.tableCell, width: "30px", padding: "12px 2px", textAlign: "center"}}>{index + 1}</td>
                            <td style={{...tableStyles.tableCell, fontWeight: "600", minWidth: "75px"}}>
                              #{order?.order_number || order?._id?.slice(-8) || "N/A"}
                            </td>
                            <td style={tableStyles.tableCell}>
                              <Chip
                                label={order?.orderSrc || "Agent"}
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                  letterSpacing: "0.2px",
                                  textTransform: "uppercase",
                                  backgroundColor: "#dbeafe",
                                  color: "#1e40af",
                                  border: "1px solid #bfdbfe",
                                }}
                              />
                            </td>
                            <td style={{...tableStyles.tableCell, color: "#059669", fontWeight: "700", minWidth: "70px"}}>
                              {order?.totalPrice
                                ?.toString()
                                ?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </td>
                            <td style={{...tableStyles.tableCell, fontSize: "11px", maxWidth: "80px"}}>
                              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {order?.shippingAddress?.locationName?.name ||
                                  order?.shippingAddress?.address?.address ||
                                  order?.shippingAddress?.description ||
                                  "N/A"}
                              </div>
                            </td>
                            <td style={{...tableStyles.tableCell, minWidth: "60px"}}>
                              {order?.paymentMethod}
                            </td>
                            <td style={{...tableStyles.tableCell, minWidth: "85px"}}>
                              {order?.shippingAddress?.customerNumber || "N/A"}
                            </td>
                            <td style={{...tableStyles.tableCell, minWidth: "85px"}}>
                              {order?.client_id?.name || "N/A"}
                            </td>
                            <td style={{...tableStyles.tableCell, fontSize: "11px", minWidth: "75px", whiteSpace: "nowrap"}}>
                              {moment(order?.createdAt).format("MMM DD, YY")}
                            </td>
                            <td style={tableStyles.tableCell}>
                              {(() => {
                                const isInHouse = order?.orderSrc === "IN-HOUSE" || order?.orderSrc === "in-house";
                                const isMomo = order?.paymentMethod === "MOMO";
                                const isPaid = order?.isPaid || isInHouse;
                                const momoFailed = order?.momo_id?.status === "Failed";
                                const label = isMomo
                                  ? (order?.momo_id?.status || "PENDING")
                                  : isInHouse
                                  ? "PAID"
                                  : (order?.isPaid ? "PAID" : "PENDING");
                                const isSuccess = isPaid && !momoFailed;
                                return (
                                  <Chip
                                    label={label}
                                    style={{
                                      padding: "2px 6px",
                                      borderRadius: "10px",
                                      fontSize: "9px",
                                      fontWeight: "700",
                                      letterSpacing: "0.2px",
                                      textTransform: "uppercase",
                                      backgroundColor: isSuccess ? "#d1fae5" : "#fee2e2",
                                      color: isSuccess ? "#065f46" : "#991b1b",
                                      border: isSuccess ? "1px solid #a7f3d0" : "1px solid #fecaca",
                                    }}
                                  />
                                );
                              })()}
                            </td>
                            <td style={tableStyles.tableCell}>
                              <Chip
                                label={order?.delivery_status}
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                  fontSize: "9px",
                                  fontWeight: "700",
                                  letterSpacing: "0.2px",
                                  textTransform: "uppercase",
                                  backgroundColor:
                                    order.delivery_status === "DELIVERED"
                                      ? "#d1fae5"
                                      : order.delivery_status === "REJECTED"
                                      ? "#fee2e2"
                                      : "#dbeafe",
                                  color:
                                    order.delivery_status === "DELIVERED"
                                      ? "#065f46"
                                      : order.delivery_status === "REJECTED"
                                      ? "#991b1b"
                                      : "#1e40af",
                                  border:
                                    order.delivery_status === "DELIVERED"
                                      ? "1px solid #a7f3d0"
                                      : order.delivery_status === "REJECTED"
                                      ? "1px solid #fecaca"
                                      : "1px solid #bfdbfe",
                                }}
                              />
                            </td>
                            <td style={tableStyles.tableCell}>
                              <Button
                                style={tableStyles.actionButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  dispatch(getOrderDetailsToProcess(order?._id));
                                  handleOrderManage(order);
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#2d3748";
                                  e.currentTarget.style.color = "#ffffff";
                                  e.currentTarget.style.borderColor = "#2d3748";
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.color = "#4a5568";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "none";
                                }}
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div style={{ padding: "24px 32px", borderTop: "2px solid #f0f2f5" }}>
                    <Button
                      onClick={() => history.push("/admin/shopCards")}
                      style={{
                        padding: "12px 28px",
                        borderRadius: "8px",
                        backgroundColor: "#2d3748",
                        color: "white",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "14px",
                        transition: "all 0.25s ease",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1a202c";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#2d3748";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                      }}
                    >
                      <i className="nc-icon nc-simple-add" style={{ marginRight: "8px" }}></i>
                      New Order
                    </Button>
                  </div>

                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default MobileWebOrderTable;
